/**
 * @desc cardtesian coord
 */
import { cross, angle as vec2Angle } from '@byted/light-render/lib/utils/vec2';
import { degToRad, inRange, radToDeg } from '../../utils/math';
/**
 * default class
 */
var Polar = /** @class */ (function () {
    function Polar(angleAxis, radiusAxis, cx, cy, radius, innerRadius) {
        this.coordinateSystem = 'polar';
        this.dimensions = ['angle', 'radius'];
        var startAngle = angleAxis.scale.start;
        var endAngle = angleAxis.scale.end;
        this._angleAxis = angleAxis;
        this._radiusAxis = radiusAxis;
        this._cx = cx;
        this._cy = cy;
        this._radius = radius;
        this._innerRadius = innerRadius;
        this._startAngle = startAngle;
        this._endAngle = endAngle;
        if (Math.abs(endAngle - startAngle) >= 360) {
            this._endAngle = endAngle > startAngle ? startAngle + 360 : startAngle - 360;
        }
    }
    Polar.prototype.getCenter = function () {
        return [this._cx, this._cy];
    };
    Polar.prototype.dataToCoord = function (data) {
        return {
            angle: this._angleAxis.scale.scale(data.angle),
            radius: this._radiusAxis.scale.scale(data.radius),
        };
    };
    Polar.prototype.coordToPoint = function (coord) {
        var x = this._cx + coord.radius * Math.cos((coord.angle * Math.PI) / 180);
        var y = this._cy + coord.radius * Math.sin((coord.angle * Math.PI) / 180);
        return {
            x: x,
            y: y,
            outside: !(inRange(coord.angle, [this._startAngle, this._endAngle]) &&
                inRange(coord.radius, [this._radius, this._innerRadius])),
        };
    };
    Polar.prototype.dataToPoint = function (data) {
        return this.coordToPoint(this.dataToCoord(data));
    };
    Polar.prototype.pointToData = function (x, y) {
        return this.coordToData(this.pointToCoord(x, y));
    };
    Polar.prototype.pointToCoord = function (x, y) {
        var dx = x - this._cx;
        var dy = y - this._cy;
        var radius = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
        var midAngle = (this._startAngle + this._endAngle) / 2;
        var midx = Math.cos(degToRad(midAngle));
        var midy = Math.sin(degToRad(midAngle));
        var angletoMiddlePoint = radToDeg(vec2Angle([dx, dy], [midx, midy]));
        var isClockwise = cross([dx, dy], [midx, midy]) < 0;
        var angle = midAngle + (isClockwise ? angletoMiddlePoint : -angletoMiddlePoint);
        return { radius: radius, angle: angle };
    };
    Polar.prototype.coordToData = function (coord) {
        return {
            angle: this._angleAxis.scale.invert(coord.angle),
            radius: this._radiusAxis.scale.invert(coord.radius),
        };
    };
    Polar.prototype.recordToCoord = function (record, dataIndex, encode) {
        return this.dataToCoord({
            angle: this._angleAxis.isCategroy() ? dataIndex : encode.angle,
            radius: this._radiusAxis.isCategroy() ? dataIndex : encode.radius,
        });
    };
    Polar.prototype.recordToPoint = function (record, dataIndex, encode) {
        return this.coordToPoint(this.recordToCoord(record, dataIndex, encode));
    };
    Polar.prototype.getCategoryAxis = function () {
        return this._angleAxis.isCategroy()
            ? this._angleAxis
            : this._radiusAxis.isCategroy()
                ? this._radiusAxis
                : this._angleAxis;
    };
    Polar.prototype.getValueAxis = function () {
        return this._angleAxis.isCategroy()
            ? this._radiusAxis
            : this._radiusAxis.isCategroy()
                ? this._angleAxis
                : this._radiusAxis;
    };
    Polar.prototype.valueToCoord = function (value, dataIndex) {
        var categoryAxis = this.getCategoryAxis();
        if (categoryAxis.extraInfo.axis === 'angleAxis') {
            return this.dataToCoord({
                angle: dataIndex,
                radius: value,
            });
        }
        return this.dataToCoord({
            angle: value,
            radius: dataIndex,
        });
    };
    return Polar;
}());
export default Polar;
